import Languages from "@data/languages.json";
import type Language from "@interfaces/Language";
import { useAuthenticationStore } from "@stores/authentication";
import { getApiBaseUrl } from "@utilities/base-url";
import { clearUserData } from "@utilities/helper";
import { default as defaultAxios } from "axios";

/**
 * Force a pathname to leading- and trailing-slash form so comparisons do not
 * depend on how a route happens to be written. Gatsby serves these routes with
 * a trailing slash ("/oauth/") while the entries below are written without one.
 */
const normalisePathname = (pathname: string): string => {
  const withLeading = pathname.startsWith("/") ? pathname : `/${pathname}`;

  return withLeading.endsWith("/") ? withLeading : `${withLeading}/`;
};

/**
 * Pages where an expired token must clear the session in place rather than
 * bounce the user to /login?expired=1.
 *
 * "/oauth" is the OAuth entry point for the ChatGPT and Claude connectors. Its
 * URL carries client_id, redirect_uri, state and code_challenge; redirecting to
 * /login discards all of them, stranding the user mid-authorisation with no way
 * back into the flow. Reloading keeps the query string intact and re-renders
 * the sign-in form so the user can carry on.
 */
const autoLogoutPathname: string[] = ["/", "/oauth"];

// Normalise the base entries too, not only the language-prefixed copies.
// Normalising inside the loop alone is what left "/oauth" in the list without a
// trailing slash: the default-language page never matched "/oauth/" and fell
// through to the redirect, while "/fr/oauth/" and the rest worked correctly.
const autoLogoutPathnameWithLanguages: string[] =
  autoLogoutPathname.map(normalisePathname);

Languages.filter((lang: Language) => lang.enabled).forEach((lang: Language) => {
  autoLogoutPathname.forEach((pathname: string) => {
    autoLogoutPathnameWithLanguages.push(
      normalisePathname(`${lang.id}${pathname}`),
    );
  });
});

const getHeaders = (): Record<string, string> => {
  const defaultHeaders: Record<string, string> = {
    Accept: "application/json",
    "Content-Type": "multipart/form-data",
  };

  return defaultHeaders;
};

const axios = defaultAxios.create({
  baseURL: `${
    getApiBaseUrl()?.endsWith("/") ? getApiBaseUrl() : getApiBaseUrl() + "/"
  }${process.env.GATSBY_API_VERSION ?? ""}`,
  timeout: 30000, // 30 seconds timeout
  headers: getHeaders(),
});

axios.interceptors.request.use(config => {
  const authenticationState = useAuthenticationStore.getState();
  if (authenticationState.isAuthenticated() && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${authenticationState.getToken()}`;
  }
  return config;
});

axios.interceptors.response.use(
  response => {
    return response;
  },
  async error => {
    // The SSO redeem's 903 ("Handoff code expired or already used") is about
    // the app's one-shot code, not the web session: signing the visitor out
    // and reloading for it would only replay the same dead code.
    const isSsoRedeem = String(error.config?.url ?? "").includes(
      "web_sso_handoff",
    );
    if (error.response?.status === 903 && !isSsoRedeem) {
      const errorMessage = String(
        error.response.data?.message ?? "",
      ).toLowerCase();
      if (errorMessage.includes("token") || errorMessage.includes("expired")) {
        if (
          typeof window !== "undefined" &&
          window.location?.pathname &&
          window.location?.reload &&
          autoLogoutPathnameWithLanguages.includes(
            normalisePathname(window.location.pathname),
          )
        ) {
          clearUserData();
          window.location.reload();
        } else {
          window.location.href = "/login?expired=1";
        }
      }
    }
    // Surface the API's own reason. axios sets error.message to the generic
    // "Request failed with status code 403" while the backend puts the real
    // text ("Your card was declined.") in the JSON body — and every
    // toast.error(error.message) call site was showing the generic one.
    const apiMessage = error.response?.data?.message;
    if (typeof apiMessage === "string" && apiMessage.trim() !== "") {
      error.message = apiMessage;
    }
    return await Promise.reject(error);
  },
);

export default axios;
